import languageData from "./content_language_data.json" with { type: "json" };

const ALIASES = languageData.aliases;
const DISPLAY_NAMES = languageData.displayNames;
const LANGUAGE_SCRIPTS = languageData.scripts;
const LATIN_WORDS = languageData.words;
const LATIN_PHRASES = languageData.phrases;

const SCRIPT_PATTERNS = {
  han: /\p{Script=Han}/gu,
  hiragana: /\p{Script=Hiragana}/gu,
  katakana: /\p{Script=Katakana}/gu,
  hangul: /\p{Script=Hangul}/gu,
  arabic: /\p{Script=Arabic}/gu,
  hebrew: /\p{Script=Hebrew}/gu,
  cyrillic: /\p{Script=Cyrillic}/gu,
  thai: /\p{Script=Thai}/gu,
  greek: /\p{Script=Greek}/gu,
  devanagari: /\p{Script=Devanagari}/gu,
  latin: /\p{Script=Latin}/gu,
};

function countMatches(text, pattern) {
  const matches = text.match(pattern);
  return matches ? matches.length : 0;
}

function preparedText(text) {
  return String(text || "")
    .replace(/https?:\/\/\S+/gi, " ")
    .replace(/\b[\w.+-]+@[\w.-]+\.[a-z]{2,}\b/gi, " ")
    .replace(/[’']/g, "");
}

// Drops tracking links and bare URL lines so a translated marketing email
// reads as the message, while the original email keeps its links.
export function readableTranslationText(text) {
  return String(text || "")
    .replace(/<?https?:\/\/\S+>?/gi, " ")
    .split("\n")
    .map((line) => line
      .replace(/^[<>\s,，]+/, "")
      .replace(/[<>\s]+$/, "")
      .replace(/\s{2,}/g, " ")
      .trim())
    .filter(Boolean)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function canonicalLanguageName(language) {
  const key = String(language || "").trim().toLowerCase();
  if (!key) return "";
  return ALIASES[key] || key;
}

export function languageDisplayName(language) {
  const canonical = canonicalLanguageName(language);
  if (!canonical) return "";
  if (DISPLAY_NAMES[canonical]) return DISPLAY_NAMES[canonical];
  return canonical.charAt(0).toUpperCase() + canonical.slice(1);
}

export function languagesDiffer(left, right) {
  const a = canonicalLanguageName(left);
  const b = canonicalLanguageName(right);
  return Boolean(a && b && a !== b);
}

function scriptCounts(text) {
  return Object.fromEntries(
    Object.entries(SCRIPT_PATTERNS).map(([script, pattern]) => [script, countMatches(text, pattern)]),
  );
}

function nonLatinLanguage(text) {
  const counts = scriptCounts(text);
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  if (total < 2) return "";

  const kana = counts.hiragana + counts.katakana;
  const han = counts.han;
  if (kana >= 2 && (kana + han) >= total * 0.5) return "Japanese";

  const candidates = [
    ["Chinese", han],
    ["Korean", counts.hangul],
    ["Arabic", counts.arabic],
    ["Hebrew", counts.hebrew],
    ["Russian", counts.cyrillic],
    ["Thai", counts.thai],
    ["Greek", counts.greek],
    ["Hindi", counts.devanagari],
  ];
  candidates.sort((left, right) => right[1] - left[1]);
  const [language, count] = candidates[0];
  if (count < 2 || count < total * 0.4) return "";
  return language;
}

function latinLanguage(text) {
  const letters = countMatches(text, SCRIPT_PATTERNS.latin);
  if (letters < 8) return "";

  const folded = text.toLowerCase();
  const tokens = folded.match(/\p{L}+/gu) || [];
  const scores = {};
  Object.entries(LATIN_WORDS).forEach(([language, words]) => {
    const wordSet = new Set(words);
    scores[language] = tokens.reduce((sum, token) => sum + (wordSet.has(token) ? 1 : 0), 0);
  });
  Object.entries(LATIN_PHRASES).forEach(([language, phrases]) => {
    phrases.forEach((phrase) => {
      if (folded.includes(phrase.replace(/[’']/g, ""))) scores[language] += 3;
    });
  });

  const ranked = Object.entries(scores).sort((left, right) => right[1] - left[1]);
  const [winner, best] = ranked[0];
  const second = ranked[1]?.[1] ?? 0;
  if (best < 2) return "";
  if (second === 0 || best >= second + 2) return languageDisplayName(winner);
  return "";
}

// English name of the language the text is written in, or "" when it is too
// short or too mixed to tell. A non-Latin script wins when it is a real share
// of the text, so a Chinese email that includes a short English footer stays
// Chinese. Same-script languages outside the word lists stay unrecognized.
export function detectContentLanguage(text) {
  const sample = preparedText(text);
  if (sample.trim().length < 2) return "";
  return nonLatinLanguage(sample) || latinLanguage(sample);
}

// Language of something the user wrote to the assistant. A clear Latin language
// wins even when they pasted a message in another script.
export function detectConversationLanguage(text) {
  const sample = preparedText(text);
  if (sample.trim().length < 2) return "";
  return latinLanguage(sample) || nonLatinLanguage(sample);
}

function scriptForLanguage(language) {
  return LANGUAGE_SCRIPTS[canonicalLanguageName(language)] || "";
}

function dominantScript(text) {
  const counts = scriptCounts(preparedText(text));
  const ranked = Object.entries(counts).sort((left, right) => right[1] - left[1]);
  const [script, count] = ranked[0];
  const second = ranked[1]?.[1] ?? 0;
  if (count < 2 || count <= second) return "";
  if (script === "hiragana" || script === "katakana") return "japanese";
  return script;
}

const SCRIPT_DISPLAY = {
  han: "Chinese",
  japanese: "Japanese",
  hangul: "Korean",
  arabic: "Arabic",
  hebrew: "Hebrew",
  cyrillic: "Russian",
  thai: "Thai",
  greek: "Greek",
  devanagari: "Hindi",
  latin: "",
};

// Languages for the draft-review translate control.
// Stamped languages win when both are present. An auto-sent reply can be
// stored without them; then the draft text is compared with the language the
// user uses with the assistant, so the control still appears when they differ.
export function draftReviewLanguages({
  draftLanguage = "",
  conversationLanguage = "",
  fallbackConversationLanguage = "",
  text = "",
} = {}) {
  if (languagesDiffer(draftLanguage, conversationLanguage)) {
    return {
      draftLanguage: languageDisplayName(draftLanguage),
      conversationLanguage: languageDisplayName(conversationLanguage),
      persisted: true,
    };
  }
  if (canonicalLanguageName(draftLanguage) && canonicalLanguageName(conversationLanguage)) return null;

  const target = canonicalLanguageName(conversationLanguage) || canonicalLanguageName(fallbackConversationLanguage);
  if (!target) return null;

  const detected = translationSourceLanguage(text, target);
  if (!detected) return null;

  return {
    draftLanguage: languageDisplayName(draftLanguage) || detected,
    conversationLanguage: languageDisplayName(target),
    persisted: false,
  };
}

// Language to name on a timeline message when it should be offered in the
// user's language. Empty when the message is already in that language, or
// when the message language cannot be told apart from it.
export function translationSourceLanguage(text, userLanguage) {
  const user = canonicalLanguageName(userLanguage);
  if (!user || !String(text || "").trim()) return "";

  const detected = detectContentLanguage(text);
  if (detected) return languagesDiffer(detected, user) ? detected : "";

  const textScript = dominantScript(text);
  const userScript = scriptForLanguage(user);
  if (!textScript || !userScript || textScript === userScript) return "";
  return SCRIPT_DISPLAY[textScript] || "";
}
